import { useState } from 'react';
import { useUserProfile } from '@/hooks/useUserProfile';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { MapPin, Shield, EyeOff } from 'lucide-react';
import { toast } from 'sonner';

export const LocationSharingSettings = () => {
  const { profile, updateProfile } = useUserProfile();
  const [loading, setLoading] = useState(false);

  const handleToggle = async (enabled: boolean) => {
    setLoading(true);
    const result = await updateProfile({
      location_sharing_consent: enabled,
      location_consent_updated_at: new Date().toISOString()
    } as any);
    
    if (result.error) {
      toast.error('Failed to update location sharing preference');
    } else {
      toast.success(
        enabled 
          ? 'Location sharing enabled'
          : 'Location sharing disabled. Future logins will not track your location.'
      );
    }
    setLoading(false);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <MapPin className="w-5 h-5" />
          Location Sharing
        </CardTitle>
        <CardDescription>
          Control whether we track your country and city for security monitoring
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <Label>Share My Location</Label>
            <p className="text-sm text-muted-foreground">
              Currently: {
                profile?.location_sharing_consent === null 
                  ? 'Not set'
                  : profile?.location_sharing_consent 
                    ? 'Enabled' 
                    : 'Disabled'
              }
            </p>
          </div>
          <Switch
            checked={profile?.location_sharing_consent === true}
            onCheckedChange={handleToggle}
            disabled={loading}
          />
        </div>
        
        {profile?.location_sharing_consent === true && (
          <Alert>
            <Shield className="h-4 w-4" />
            <AlertTitle>Enhanced Security</AlertTitle>
            <AlertDescription>
              We'll alert you if suspicious logins are detected from unusual locations.
            </AlertDescription>
          </Alert>
        )}
        
        {profile?.location_sharing_consent === false && (
          <Alert>
            <EyeOff className="h-4 w-4" />
            <AlertTitle>Location Hidden</AlertTitle>
            <AlertDescription>
              Your location is shown as "Hidden, Unknown" in all systems.
            </AlertDescription>
          </Alert>
        )}
        
        <div className="pt-2 border-t">
          <p className="text-xs text-muted-foreground">
            <strong>Privacy Note:</strong> We only store your country and city. 
            Your IP address is anonymized immediately. You can change this anytime.
          </p>
        </div>
      </CardContent>
    </Card>
  );
};
